import { io, Socket } from 'socket.io-client';
import type {
  WalletBalanceUpdatedEvent,
  TransactionReceivedEvent,
  TransactionSentEvent,
  AppNotification,
} from '../types/notification.types';

const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL ||
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace('/api/v1', '') ||
  'http://localhost:3000';

// ─── Re-export event payload types ───────────────────────────────────────────

export type { WalletBalanceUpdatedEvent, TransactionReceivedEvent, TransactionSentEvent };
export type NotificationPayload = AppNotification;

// Legacy float-recharge shapes (backwards compat with useFloatRechargeSocket)
export type RechargeApprovedPayload = AppNotification & {
  type: 'float:recharge_approved';
  data: Record<string, unknown> & {
    requestId: string;
    amount: number;
    newBalance: number;
    currency: string;
    approvedAt: string;
  };
};

export type RechargeRejectedPayload = AppNotification & {
  type: 'float:recharge_rejected';
  data: Record<string, unknown> & {
    requestId: string;
    amount: number;
    reason: string;
    currency: string;
    rejectedAt: string;
  };
};

// ─── Singleton ────────────────────────────────────────────────────────────────

let socket: Socket | null = null;

export function getSocket(): Socket | null {
  return socket;
}

export function connectSocket(token: string): Socket {
  // If a connected/connecting socket already exists, reuse it
  if (socket && (socket.connected || socket.active)) {
    return socket;
  }

  // Destroy stale socket
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 30_000,
  });

  if (import.meta.env.DEV) {
    socket.on('connect', () => console.log('[Socket] Connected:', socket?.id));
    socket.on('disconnect', (r) => console.log('[Socket] Disconnected:', r));
    socket.on('connect_error', (e) => console.warn('[Socket] Error:', e.message));
  }

  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

export function isSocketConnected(): boolean {
  return socket?.connected ?? false;
}
